import { type Ref, type ComputedRef, ref, computed } from 'vue';
import { type ExposeInterface, WatchedRange } from './types';
import { AppConstants } from "@/common";
import { TrickModeRestriction, RangeProgress, RangeProperties, type ContentType } from '@ca.vmedia.tv/domains';
import { Services } from "@ca.vmedia.tv/services";
import { Logger } from '@/utils';
import type { AxiosError } from 'axios';
import type { PlayItemRestrictionsProcessor } from '@/components/Player/types/PlayItemRestrictionsProcessor';

export const useWatchedRange = (itemProcessor: ComputedRef<PlayItemRestrictionsProcessor | null>): ExposeInterface => {

    const _watchedRange: Ref<WatchedRange | null> = ref(null);
    const streamWatchedRanges = computed((): RangeProperties[] => _watchedRange.value?.range ?? []);
    const streamWatchedRangeId = computed((): number | null => _watchedRange.value?.id ?? null);

    const playItemId = computed((): number | null => itemProcessor.value?.playItemId ?? null);
    const restrictions = computed((): TrickModeRestriction => itemProcessor.value?.getRestrictions ?? TrickModeRestriction.None);
    const startTimeSec = computed((): number => itemProcessor.value?.startTimeSec ?? 0);
    const lengthInS = computed((): number | null => itemProcessor.value?.lengthInS ?? null);
    const contentType = computed((): ContentType | null => itemProcessor.value?.contentType ?? null);
    const endTimeSec = computed((): number => itemProcessor.value?.isVOD ? startTimeSec.value + (lengthInS.value ?? 0) : itemProcessor.value?.endTimeSec ?? 0);

    const updateWatchedRangeState = (newWatchedRange: WatchedRange): void => {
        _watchedRange.value = newWatchedRange;
    }

    const _calculateRangeStart = (currentTime: number): number => {
        return Math.min(1, Math.max(0, currentTime / Number(lengthInS.value))) * 100;
    };

    const getSegmentsWR = computed((): RangeProgress[] => {
        if (playItemId.value === null || playItemId.value !== streamWatchedRangeId.value) {
            return [];
        }
        
        const segments = streamWatchedRanges.value.map((item) => {
            if (!item.Start || !item.End) {
                return new RangeProgress();
            }
            const startProgress = item.Start - startTimeSec.value;
            const start = _calculateRangeStart(startProgress);
            const width = (item.End - item.Start) * 100 / (lengthInS.value || 0);
            return new RangeProgress(start, width > 100 ? 100 : width);
        }) ?? [];
        
        return segments;
    });

    const availabilityFF = (currentTime: number): boolean => {
        if (streamWatchedRanges.value.length > 0 && restrictions.value !== TrickModeRestriction.None) {
            return streamWatchedRanges.value.some((rangeItem) => !!rangeItem.Start && !!rangeItem.End && currentTime > rangeItem.Start && currentTime < (rangeItem.End - AppConstants.defaultSkipTime));
        }
        return false;
    };

    const availabilityRW = (currentTime: number): boolean => {
        if (streamWatchedRanges.value.length > 0 && restrictions.value !== TrickModeRestriction.None) {
            return streamWatchedRanges.value.some((rangeItem) => !!rangeItem.Start && !!rangeItem.End && currentTime > (rangeItem.Start + AppConstants.defaultSkipTime) && currentTime < rangeItem.End);
        }
        return false;
    };

    const sendWatchedRange = async (rangeProperties: RangeProperties): Promise<void> => {
        try {
            if (!playItemId.value || !contentType.value) {
                throw new Error(!playItemId.value ? "Item id is not defined" : "Item type is not defined");
            }
            if (!rangeProperties.Start || !rangeProperties.End) {
                throw new Error("Range properties are not defined");
            }
            await Services.Instance.domains.mediaRepository.setWatchedRange(playItemId.value, contentType.value, rangeProperties.Start, rangeProperties.End);
        } catch (error) {
            Logger.captureError(error as AxiosError);
        }
    };

    const setWatchedRange = async (startTime: number, endTime: number): Promise<void> => {
        if (!startTime || restrictions.value === TrickModeRestriction.None || !itemProcessor.value || startTime >= endTime || startTime < startTimeSec.value) {
            return;
        }
        if (!playItemId.value || !contentType.value) {
            throw new Error(!playItemId.value ? "Item id is not defined" : "Item type is not defined");
        }
        const end = endTime > endTimeSec.value ? endTimeSec.value : endTime;
        const newRangeItem = new RangeProperties(startTime, end);
        if (streamWatchedRangeId.value === playItemId.value && streamWatchedRanges.value.length > 0) {
            const rangeItemForUpdateIndex = streamWatchedRanges.value.findIndex((item) => !!item.Start && !!item.End && item.Start <= startTime && item.End >= startTime);
            if (rangeItemForUpdateIndex !== -1) {
                const rangeItemForUpdate = streamWatchedRanges.value[rangeItemForUpdateIndex];
                if (rangeItemForUpdate.End && rangeItemForUpdate.End > end) {
                    return;
                }
                if (rangeItemForUpdate.Start && rangeItemForUpdate.Start < startTimeSec.value) {
                    rangeItemForUpdate.Start = startTimeSec.value;
                }
                rangeItemForUpdate.End = end;
                await sendWatchedRange(rangeItemForUpdate);
                const newRanges = [...streamWatchedRanges.value];
                newRanges.splice(rangeItemForUpdateIndex, 1, rangeItemForUpdate);
                updateWatchedRangeState(new WatchedRange(
                    playItemId.value,
                    contentType.value,
                    newRanges
                ));
                return;
                
            }
            await sendWatchedRange(newRangeItem);
            updateWatchedRangeState(new WatchedRange(
                playItemId.value,
                contentType.value,
                [
                    ...streamWatchedRanges.value,
                    newRangeItem
                ]
            ));
        } else {
            await sendWatchedRange(newRangeItem);
            updateWatchedRangeState(new WatchedRange(
                playItemId.value,
                contentType.value,
                [newRangeItem]
            ));
        }
    };

    const getWatchedRange = async (): Promise<void> => {
        if (itemProcessor.value !== null && restrictions.value !== TrickModeRestriction.None) {
            try {
                if (!playItemId.value || !contentType.value) {
                    throw new Error(!playItemId.value ? "Item id is not defined" : "Item type is not defined");
                }
                const properties = await Services.Instance.domains.mediaRepository.getWatchedRange(playItemId.value, contentType.value);
                updateWatchedRangeState(new WatchedRange(playItemId.value, contentType.value, properties.Result));
            } catch (error) {
                Logger.captureError(error as AxiosError);
            }
        }
    };

    const clearWatchedRange = (): void => {
        _watchedRange.value = null;
    };

    return {
        getSegmentsWR,
        setWatchedRange,
        clearWatchedRange,
        getWatchedRange,
        availabilityFF,
        availabilityRW,
    }
};
